import { useEffect, useState } from "react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { api, cedi, errMsg } from "@/lib/api";

const Impact = ({ n, isSA, permanent, error }) => {
  if (error) return <span role="alert" className="text-[#B30000]" data-testid="event-delete-error">Could not check linked records: {error}. Close and try again.</span>;
  if (!n) return <span data-testid="event-delete-loading">Checking linked records…</span>;
  const total = n.registrations + n.deleted_registrations;
  return (
    <span className="block space-y-3" data-testid="event-delete-impact">
      <span className="block">{permanent ? "This cannot be undone. The event and all linked registrations, payments, refunds, receipts, drafts and room bookings will be erased. Cash report history will change. This does not issue refunds or delete member accounts." : "The event will be hidden from the public. Its setup and linked records remain saved, read-only. You can undelete it later; it returns unpublished."}</span>
      <span className="block rounded-md bg-red-50 border border-red-200 text-red-800 px-3 py-2 text-xs">
        Registrations: {total} · Drafts: {n.drafts} · Payment/refund records: {n.payments} · Special receipts: {n.special_receipts || 0}. Amount still paid: {cedi(n.paid_total)}
      </span>
      {!isSA && total > 0 && <span className="block font-semibold text-[#B30000]">Only the Super Admin can delete an event that has registrations.</span>}
    </span>
  );
};

export const DeleteEventDialog = ({ event, isSA, onClose, onConfirm }) => {
  const [n, setN] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const permanent = !!event?.deleted;
  useEffect(() => {
    let active = true;
    setN(null); setError("");
    if (event) api.get(`/events/${event.id}/delete-impact`).then((r) => { if (active) setN(r.data); }).catch((e) => { if (active) setError(errMsg(e)); });
    return () => { active = false; };
  }, [event]);
  const blocked = !n || busy || (!isSA && (permanent || n.registrations + n.deleted_registrations > 0));
  const confirm = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await onConfirm(event); onClose(); } catch (err) { toast.error(errMsg(err)); }
    finally { setBusy(false); }
  };
  return (
    <AlertDialog open={!!event} onOpenChange={(o) => !o && !busy && onClose()}>
      <AlertDialogContent className="w-[calc(100%-2rem)] rounded-lg max-h-[90vh] overflow-y-auto" data-testid="event-delete-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle data-testid="event-delete-title">{permanent ? "Permanently delete" : "Move to Deleted:"} "{event?.title}"?</AlertDialogTitle>
          <AlertDialogDescription asChild><div className="text-sm text-gray-700"><Impact n={n} isSA={isSA} permanent={permanent} error={error} /></div></AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter className="flex-col sm:flex-row gap-2 sm:space-x-0">
          <AlertDialogCancel className="mt-0" disabled={busy} data-testid="event-delete-no">Cancel</AlertDialogCancel>
          <AlertDialogAction disabled={blocked} onClick={confirm} className="bg-[#E60000] hover:bg-[#CC0000]" data-testid="event-delete-yes">{busy ? "Deleting…" : permanent ? "Permanently delete" : "Move to Deleted"}</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
